import type {
  CreatePlanLockInput,
  CreateSojournInput,
  DateRange,
  GeneratePlanInput,
  SetSojournDayNoteInput,
  UpdateSojournInput,
} from '@room-mates/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../../shared/api/queryKeys.js';
import { isApiError } from '../../shared/api/apiError.js';
import { useApi } from '../../shared/auth/useAuth.js';

/**
 * Anything that changes who or what takes part invalidates the stored plan on
 * the server, so the client drops its copy at the same moment rather than
 * showing an arrangement built from facts that no longer hold.
 */
function useSojournInvalidation(sojournId?: string): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.sojourns.all });
    if (sojournId !== undefined) {
      await queryClient.invalidateQueries({ queryKey: queryKeys.sojourns.detail(sojournId) });
      await queryClient.invalidateQueries({ queryKey: queryKeys.sojourns.locks(sojournId) });
      await queryClient.invalidateQueries({ queryKey: queryKeys.sojourns.plan(sojournId) });
    }
  };
}

export function useSojourns() {
  const api = useApi();
  return useQuery({ queryKey: queryKeys.sojourns.all, queryFn: () => api.sojourns.list() });
}

export function useSojourn(sojournId: string) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.sojourns.detail(sojournId),
    queryFn: () => api.sojourns.get(sojournId),
  });
}

export function useCreateSojourn() {
  const api = useApi();
  const invalidate = useSojournInvalidation();
  return useMutation({
    mutationFn: (input: CreateSojournInput) => api.sojourns.create(input),
    onSuccess: invalidate,
  });
}

export function useUpdateSojourn(sojournId: string) {
  const api = useApi();
  const invalidate = useSojournInvalidation(sojournId);
  return useMutation({
    mutationFn: (input: UpdateSojournInput) => api.sojourns.update(sojournId, input),
    onSuccess: invalidate,
  });
}

export function useDeleteSojourn() {
  const api = useApi();
  const invalidate = useSojournInvalidation();
  return useMutation({
    mutationFn: (sojournId: string) => api.sojourns.remove(sojournId),
    onSuccess: invalidate,
  });
}

export function useSetSojournHouse(sojournId: string) {
  const api = useApi();
  const invalidate = useSojournInvalidation(sojournId);
  return useMutation({
    mutationFn: ({ houseId, availability }: { houseId: string; availability?: DateRange[] }) =>
      api.sojourns.setHouse(sojournId, houseId, availability === undefined ? {} : { availability }),
    onSuccess: invalidate,
  });
}

export function useRemoveSojournHouse(sojournId: string) {
  const api = useApi();
  const invalidate = useSojournInvalidation(sojournId);
  return useMutation({
    mutationFn: (houseId: string) => api.sojourns.removeHouse(sojournId, houseId),
    onSuccess: invalidate,
  });
}

export function useSetSojournGuest(sojournId: string) {
  const api = useApi();
  const invalidate = useSojournInvalidation(sojournId);
  return useMutation({
    mutationFn: ({ guestId, stays }: { guestId: string; stays?: DateRange[] }) =>
      api.sojourns.setGuest(sojournId, guestId, stays === undefined ? {} : { stays }),
    onSuccess: invalidate,
  });
}

export function useRemoveSojournGuest(sojournId: string) {
  const api = useApi();
  const invalidate = useSojournInvalidation(sojournId);
  return useMutation({
    mutationFn: (guestId: string) => api.sojourns.removeGuest(sojournId, guestId),
    onSuccess: invalidate,
  });
}

export function useSetSojournDayNote(sojournId: string) {
  const api = useApi();
  const invalidate = useSojournInvalidation(sojournId);
  return useMutation({
    mutationFn: ({ date, input }: { date: string; input: SetSojournDayNoteInput }) =>
      api.sojourns.setDayNote(sojournId, date, input),
    onSuccess: invalidate,
  });
}

export function usePlanLocks(sojournId: string) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.sojourns.locks(sojournId),
    queryFn: () => api.locks.list(sojournId),
  });
}

export function useCreatePlanLock(sojournId: string) {
  const api = useApi();
  const invalidate = useSojournInvalidation(sojournId);
  return useMutation({
    mutationFn: (input: CreatePlanLockInput) => api.locks.create(sojournId, input),
    onSuccess: invalidate,
  });
}

export function useRemovePlanLock(sojournId: string) {
  const api = useApi();
  const invalidate = useSojournInvalidation(sojournId);
  return useMutation({
    mutationFn: (lockId: string) => api.locks.remove(sojournId, lockId),
    onSuccess: invalidate,
  });
}

/**
 * The stored plan, if there is one.
 *
 * "No plan yet" is an ordinary state, not an error, so a 404 resolves to `null`
 * and the page can offer the generate button instead of an alarming message.
 */
export function usePlan(sojournId: string) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.sojourns.plan(sojournId),
    queryFn: async () => {
      try {
        return await api.plans.get(sojournId);
      } catch (error) {
        if (isApiError(error) && error.status === 404) {
          return null;
        }
        throw error;
      }
    },
  });
}

export function useGeneratePlan(sojournId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: GeneratePlanInput = {}) => api.plans.generate(sojournId, input),
    onSuccess: async (plan) => {
      queryClient.setQueryData(queryKeys.sojourns.plan(sojournId), plan);
      await queryClient.invalidateQueries({ queryKey: queryKeys.sojourns.all });
    },
  });
}
